import React, { useState, useEffect } from 'react'
import { useUser } from 'util/UserContext'
import { Info, Loader2 } from 'lucide-react'
import api from 'config/axiosConfig'
import Modal from 'ui/Modal'
import { UsernameValidationResponse } from 'util/constants'
import InputField from 'ui/InputField'
import { updateDefaultAudience, updatePrivateAccountStatus } from 'util/userUtils'
import RESTRICTED_WORD_LIST from '../../config/restrictedWordList'
import { apiRoutes } from "util/apiRoutes";

interface Props {
    onClose: () => void
    userSignup?: boolean
}

const USERNAME_REQUIREMENTS = {
    minLength: 2,
    maxLength: 30
};

const UsernameModal: React.FC<Props> = ({ onClose, userSignup = false }) => {
    const { currentUser, refreshUser, setShowUserOnboardModal, setUserOnboardInitialView } = useUser()
    const isQuizAuthFlow = typeof window !== "undefined" && window.location.pathname.startsWith("/quiz")
    const [username, setUsername] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidUsername, setIsValidUsername] = useState<boolean>(false)
    const [validationMessage, setValidationMessage] = useState<string>('')
    const [isPrivate, setIsPrivate] = useState<boolean>(false)

    const validateUsername = async (username: string): Promise<void> => {
        setIsValidUsername(true);
        setValidationMessage('');
        
        if (
            /^[a-z0-9_.]{2,30}$/.test(username) &&
            !/\.{2,}/.test(username) &&
            !username.startsWith(".") &&
            !username.startsWith("_") &&
            !username.endsWith(".") &&
            !username.endsWith("_") &&
            !username.includes("..") &&
            !username.includes("__") &&
            !/^admin/.test(username) &&
            !/admin$/.test(username) &&
            !RESTRICTED_WORD_LIST.includes(username.toLowerCase())
        ) {
            try {
                // Check for existing user with this name
                const userResponse = await api.get<UsernameValidationResponse>(apiRoutes.user.byUsername(username));
                if (userResponse.status !== 404) {
                    setIsValidUsername(false);
                    setValidationMessage('Username is already taken.');
                    return;
                }

                // Check for existing team with this name
                const teamResponse = await api.get(apiRoutes.team.byName(username));
                if (teamResponse.status !== 404) {
                    setIsValidUsername(false);
                    setValidationMessage('Username conflicts with existing Partea name.');
                    return;
                }

                setIsValidUsername(true);
                setValidationMessage('');
            } catch (error: any) {
                if (error.response?.status === 404) {
                    setIsValidUsername(true);
                    setValidationMessage('');
                } else {
                    setIsValidUsername(false);
                    setValidationMessage('Error validating username.');
                }
            }
        } else {
            if (username.startsWith(".") ||
                username.startsWith("_") ||
                username.endsWith(".") ||
                username.endsWith("_")
            ) {
                setIsValidUsername(false);
                setValidationMessage(`Username must not start/end with '.' or '_'`);
            } else if (/^admin/.test(username) ||
                /admin$/.test(username) ||
                RESTRICTED_WORD_LIST.includes(username.toLowerCase())
            ) {
                setIsValidUsername(false);
                setValidationMessage('Username is not allowed.');
            } else if (!/^[a-z0-9_.]{2,30}$/.test(username)) {
                setIsValidUsername(false);
                setValidationMessage(`Username may not contain special characters other than '.' and '_'`);
            } else if (username.includes("..") ||
                username.includes("__")
            ) {
                setIsValidUsername(false);
                setValidationMessage(`Username may not contain consecutive '.' or '_'`);
            }
        }
    };

    useEffect(() => {
        if (username && username.length > 1) {
            validateUsername(username)
        } else if (username &&             
            (username.startsWith(".") ||
            username.startsWith("_"))) {
            setIsValidUsername(false)
            setValidationMessage(`Username must not start with '.' or '_'`)
        } else if (username && username.length < USERNAME_REQUIREMENTS.minLength) {
            setIsValidUsername(false)
            setValidationMessage(`Username must be at least ${USERNAME_REQUIREMENTS.minLength} characters long.`)
        } else {
            setValidationMessage('')
        }
    }, [username])

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const lowercaseUsername = e.target.value.toLowerCase()
        setUsername(lowercaseUsername)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('');
        setIsSubmitting(true);
        try {
            await api.patch(apiRoutes.user.username, { username })
            if (userSignup && isPrivate) {
                const response = await updatePrivateAccountStatus(true)
                if (response) {
                    await updateDefaultAudience('follow')
                    await refreshUser();
                    if (!isQuizAuthFlow) {
                        setUserOnboardInitialView('issues');
                        setShowUserOnboardModal(true);
                    }
                    onClose();
                }
            } else {
                await refreshUser();
                if (userSignup) {
                    if (!isQuizAuthFlow) {
                        setUserOnboardInitialView('issues');
                        setShowUserOnboardModal(true);
                    }
                    onClose();
                } else {
                    onClose();
                }
            }
        } catch (error) {
            console.error('Error updating username:', error)
            setValidationMessage('Failed to update username. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = (): void => {
        if (userSignup && !isQuizAuthFlow) {
            setUserOnboardInitialView('issues');
            setShowUserOnboardModal(true);
        } 
        
        onClose()        
    }

    const isButtonDisabled = ((!username || !isValidUsername)) ||
    isSubmitting || 
    username.length < USERNAME_REQUIREMENTS.minLength;

    const footer = (
        <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex sm:flex-row sm:justify-end sm:gap-x-2 py-2">
            <button
                type="button"
                onClick={onClose}
                className="btn-secondary w-full sm:w-fit py-2 px-4"
            >
                Cancel
            </button>
            <button
                type="submit"
                onClick={handleSubmit}
                disabled={isButtonDisabled}
                className={`w-full sm:w-fit py-2 px-4 ${
                    isButtonDisabled ? 'btn-primary-disabled' : 'btn-primary'
                } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : 'Submit'}
            </button>
        </div>
    );

    return (
        <Modal isOpen={true} onClose={handleClose} title="Set your username" footer={footer} stickyFooter>
            <div className="flex flex-col w-full sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                <div className="flex flex-col flex-grow space-y-4">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        Usernames are publicly visible.
                    </p>

                    <form onSubmit={handleSubmit} className="flex flex-col flex-grow space-y-4">
                        <div className="form-control">
                            <div className="relative">
                                <InputField
                                    type="username"
                                    id="username"
                                    name="username"
                                    labelText="Username"
                                    fieldRequired
                                    fieldPlaceholder={currentUser?.username || ''}
                                    value={username}
                                    onChange={handleUsernameChange}
                                    validationState={isValidUsername && username.length >= USERNAME_REQUIREMENTS.minLength ? 'valid' : username && !isValidUsername ? 'invalid' : 'none'}
                                    autoComplete="username"
                                    error={validationMessage}
                                    spellCheck={false}
                                    maxLength={USERNAME_REQUIREMENTS.maxLength}
                                    minLength={USERNAME_REQUIREMENTS.minLength}
                                />

                                {error && (
                                    <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                                        <Info className="mr-1 size-4" />
                                        <span>{error}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        {userSignup && (
                            <div className="grid grid-flow-col-dense gap-3 items-center justify-between w-48">
                                <label htmlFor="register" className="col-span-2 block font-semibold text-sm text-eoe-text-heading dark:text-eoe-dark-text-heading pl-1">
                                    Private account
                                </label>
                                <input
                                    type="checkbox"
                                    id="register"
                                    checked={isPrivate}
                                    onChange={(e) => {
                                        setIsPrivate(e.target.checked);
                                    }}
                                    className="toggle-switch-input col-span-1"
                                />
                            </div>
                        )}
                    </form>
                </div>

                {/* Action buttons moved to Modal footer */}
            </div>
        </Modal>
    );
};

export default UsernameModal;
